import { useState, useEffect, useCallback, useRef } from 'react';

export type LocationSource = 'gps' | 'ip' | 'manual' | 'default';

export interface UserLocation {
  lat: number;
  lon: number;
  city: string;
  region?: string;
  source: LocationSource;
}

interface StoredLocation extends UserLocation {
  storedAt: number;
}

interface UseUserLocationResult {
  location: UserLocation | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  permissionDenied: boolean;
  isUsingDefault: boolean;
  storageError: boolean;
}

const MI_BOUNDS = { latMin: 41.7, latMax: 47.5, lonMin: -90.4, lonMax: -82.4 };

function isInMichigan(lat: number, lon: number): boolean {
  return lat >= MI_BOUNDS.latMin && lat <= MI_BOUNDS.latMax
      && lon >= MI_BOUNDS.lonMin && lon <= MI_BOUNDS.lonMax;
}

const STORAGE_KEY = 'miweather_user_location';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours - force refresh after this
const STALE_THRESHOLD = 60 * 60 * 1000; // 1 hour - background refresh after this

const DEFAULT_LOCATION: UserLocation = {
  lat: 42.7325,
  lon: -84.5555,
  city: 'Lansing',
  region: 'MI',
  source: 'default',
};

// Get location from localStorage with age info
function getStoredLocation(): { location: UserLocation | null; isExpired: boolean; isStale: boolean; storageError: boolean } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data: StoredLocation = JSON.parse(stored);
      const age = Date.now() - data.storedAt;
      const isExpired = age > CACHE_TTL;
      const isStale = age > STALE_THRESHOLD;

      // Remove storedAt from returned location
      const { storedAt, ...location } = data;
      return { location, isExpired, isStale, storageError: false };
    }
  } catch (error) {
    console.warn('Failed to read location from localStorage:', error);
    return { location: null, isExpired: true, isStale: true, storageError: true };
  }
  return { location: null, isExpired: true, isStale: true, storageError: false };
}

// Save location to localStorage with timestamp, returns false on failure
function storeLocation(location: UserLocation): boolean {
  try {
    const data: StoredLocation = { ...location, storedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.warn('Failed to save location to localStorage:', error);
    return false;
  }
}

// Get location from IP (proxied through backend)
async function getLocationFromIP(): Promise<UserLocation> {
  const response = await fetch('/api/v1/geo/ip');
  if (!response.ok) {
    throw new Error('Failed to get IP location');
  }
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error || 'IP location failed');
  }

  const data = json.data;

  // Validate that lat/lon are valid numbers
  if (typeof data.lat !== 'number' || typeof data.lon !== 'number' ||
      isNaN(data.lat) || isNaN(data.lon)) {
    throw new Error('IP location returned invalid coordinates');
  }

  return {
    lat: data.lat,
    lon: data.lon,
    city: data.city || 'Unknown',
    region: data.regionName,
    source: 'ip',
  };
}

// Get location from browser Geolocation API
function getBrowserLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000, // 5 minutes
    });
  });
}

// Reverse geocode to get city name (proxied through backend)
async function reverseGeocode(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(`/api/v1/geo/reverse?lat=${lat}&lon=${lon}`);
    if (response.ok) {
      const json = await response.json();
      if (json.success && json.data?.city) {
        return json.data.city;
      }
    }
  } catch (error) {
    console.warn('Failed to reverse geocode location:', error);
  }
  return 'Your Location';
}

export function useUserLocation(): UseUserLocationResult {
  // Get initial stored location - these values are captured once on mount
  const initialStateRef = useRef(() => {
    const stored = getStoredLocation();
    return {
      storedLoc: stored.location,
      isExpired: stored.isExpired,
      isStale: stored.isStale,
      initialStorageError: stored.storageError,
    };
  });
  const { storedLoc, isExpired, isStale, initialStorageError } = initialStateRef.current();

  const [location, setLocation] = useState<UserLocation | null>(storedLoc);
  const [loading, setLoading] = useState(!storedLoc || isExpired);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isUsingDefault, setIsUsingDefault] = useState(false);
  const [storageError, setStorageError] = useState(initialStorageError);
  const isRefreshing = useRef(false);
  const hasFetchedRef = useRef(false);

  const fetchLocation = useCallback(async (showLoading = true) => {
    // Prevent concurrent refreshes
    if (isRefreshing.current) return;
    isRefreshing.current = true;

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    setIsUsingDefault(false);

    try {
      // Try browser geolocation first
      const position = await getBrowserLocation();
      const { latitude: lat, longitude: lon } = position.coords;

      if (!isInMichigan(lat, lon)) {
        setError('miweather.org only covers Michigan locations');
        setLocation(DEFAULT_LOCATION);
        setIsUsingDefault(true);
        setPermissionDenied(false);
        setLoading(false);
        isRefreshing.current = false;
        return;
      }

      const city = await reverseGeocode(lat, lon);

      const newLocation: UserLocation = {
        lat,
        lon,
        city,
        region: 'MI',
        source: 'gps',
      };

      setLocation(newLocation);
      const stored = storeLocation(newLocation);
      if (!stored) {
        setStorageError(true);
      }
      setPermissionDenied(false);
    } catch (geoError) {
      // Check if permission was denied
      if (geoError instanceof GeolocationPositionError && geoError.code === 1) {
        setPermissionDenied(true);
      }

      // Fall back to IP geolocation
      try {
        const ipLocation = await getLocationFromIP();
        setLocation(ipLocation);
        const stored = storeLocation(ipLocation);
        if (!stored) {
          setStorageError(true);
        }
      } catch (ipError) {
        console.warn('Failed to get IP-based location:', ipError);
        // Use default location as last resort - but show clear error state
        setLocation(DEFAULT_LOCATION);
        setIsUsingDefault(true);
        setError('Could not determine your location. Showing Lansing, MI.');
      }
    } finally {
      setLoading(false);
      isRefreshing.current = false;
    }
  }, []);

  useEffect(() => {
    // Only run once on mount
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // No stored location or expired - fetch with loading spinner
    if (!storedLoc || isExpired) {
      fetchLocation(true);
    }
    // Stale but not expired - background refresh (no loading spinner)
    else if (isStale) {
      fetchLocation(false);
    }
  }, [storedLoc, isExpired, isStale, fetchLocation]);

  return {
    location,
    loading,
    error,
    refresh: () => fetchLocation(true),
    permissionDenied,
    isUsingDefault,
    storageError,
  };
}
